import { useState, useEffect } from 'react';
import {
  OutlinedInput, InputLabel, Checkbox, FormControlLabel, Dialog,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { buildProjectPath, teamIsPaid } from 'lib/helpers';
import { MobileTooltip } from 'components/MobileTooltip';
import Link from 'next/link';
import { TeamSelect } from 'components/AddEditList/TeamSelect';
import { Project } from 'lib/types/projects';
import { Team } from 'lib/types/teams';

interface Props {
  onCancel: () => void;
  project: Partial<Project>;
  onSave?: () => void;
  showOptionToFeature?: boolean;
}

export const QuickAddProject = ({
  onCancel, project, onSave, showOptionToFeature = false,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState<string>(project.name || '');
  const [description, setDescription] = useState<string>(project.description || '');
  const [teamId, setTeamId] = useState<number | undefined>(undefined);
  const [isPrivate, setIsPrivate] = useState<boolean>(project.isPrivate ?? true);
  const [isFeatured, setIsFeatured] = useState<boolean>(project.featureOnProfile || false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState<boolean>(true);

  /* teams that user's a member of */
  const myTeams = teams?.filter(
    (t: Team) => t.members.find((m: any) => m.userId === currentUser?.id),
  ) || [];

  // Fetch teams data when component mounts
  useEffect(() => {
    const fetchTeams = async () => {
      if (!currentUser) {
        setTeamsLoading(false);
        return;
      }

      try {
        setTeamsLoading(true);
        const teamsData = await API.getTeams();
        setTeams(teamsData || []);
      } catch (error) {
        // Silently handle error and set empty teams array
        setTeams([]);
      } finally {
        setTeamsLoading(false);
      }
    };

    fetchTeams();
  }, [currentUser, API]);

  const togglePrivacy = (privacy: boolean) => {
    setIsPrivate(privacy);
    if (privacy) {
      setIsFeatured(false);
    }
  };
  const toggleFeatured = (featured: boolean) => {
    setIsFeatured(featured);
    if (featured) {
      setIsPrivate(false);
    }
  };

  const saveProject = async () => {
    if (!currentUser) return;

    try {
      const p = await API.addProject({
        name,
        description,
        teamId,
        ownerId: teamId ? undefined : currentUser.id,
        isPrivate,
        featureOnProfile: isFeatured,
        postIds: project.postIds || [],
      });
      const action = () => (
        <button type="button" className="btn">
          <Link href={buildProjectPath(p)}>
            View project
          </Link>
        </button>
      );
      enqueueSnackbar(`${name} has been created.`, {
        variant: 'success',
        action,
      });
      if (onSave) {
        onSave();
      }
      onCancel();
    } catch (error: any) {
      const errorAction = error.id ? () => (
        <button type="button" className="btn">
          <Link href={`/projects/${error.id}`}>
            View project
          </Link>
        </button>
      ) : undefined;

      enqueueSnackbar(`Error saving project: ${error.message}`, {
        variant: 'error',
        action: errorAction,
      });
    }
  };

  const changeTeam = (id?: number) => {
    setTeamId(id);
    if (id) {
      setIsPrivate(true);
      setIsFeatured(false);
    }
    const team = myTeams.find((t) => t.id === id);
    if (team && !teamIsPaid(team)) {
      enqueueSnackbar(`Note: The team "${team.name}" does not have an active subscription. Active your subscription to create a team project`, {
        variant: 'info',
        action: () => (
          <button type="button" className="btn">
            <Link href="/settings/billing">
              Go to Billing
            </Link>
          </button>
        ),
      });
    }
  };

  return (
    <Dialog open fullWidth>
      <div className="p-6">
        <h1 className="text-lg font-semibold mb-4">
          Create a New Project
        </h1>
        <div className="mb-4">
          <InputLabel id="project-name-label" className="font-semibold mb-2">Name</InputLabel>
          <OutlinedInput
            type="text"
            id="name"
            name="name"
            placeholder="Name"
            value={name}
            className="w-full"
            size="small"
            onChange={
              (e) => {
                setName(e.target.value);
              }
            }
          />
        </div>
        <div className="mb-4">
          <InputLabel id="project-name-label" className="font-semibold mb-2">Description</InputLabel>
          <OutlinedInput
            type="text"
            id="description"
            name="description"
            placeholder="Description"
            value={description}
            className="w-full"
            size="small"
            onChange={
              (e) => {
                setDescription(e.target.value);
              }
            }
          />
        </div>
        { !teamId && (
          <div className="mb-4">
            <FormControlLabel
              control={(
                <Checkbox
                  checked={isPrivate}
                  onChange={() => togglePrivacy(!isPrivate)}
                  name="isPrivate"
                  color="primary"
                />
              )}
              label="Private" // todo: better label?
            />
            <MobileTooltip
              placement="top"
              arrow
              title="Projects are automatically private, but you can make them public if you want to share them with others. A featured project is automatically public."
            >
              <Info color="secondary" />
            </MobileTooltip>
          </div>
        )}
        {showOptionToFeature && !teamId && (
          <div className="mb-4">
            <FormControlLabel
              control={(
                <Checkbox
                  checked={isFeatured}
                  onChange={() => toggleFeatured(!isFeatured)}
                  name="isFeatured"
                  color="primary"
                />
              )}
              label="Show on profile"
            />
            <MobileTooltip
              placement="top"
              arrow
              title="Feature this project on the top of your profile! A featured project is automatically public."
            >
              <Info color="secondary" />
            </MobileTooltip>
          </div>
        )}
        {!teamsLoading && myTeams.length >= 1 && (
          <div className="mb-4">
            <InputLabel id="team-label" className="font-semibold mb-1">Team (optional)</InputLabel>
            <TeamSelect
              teams={myTeams}
              selected={teamId}
              onChange={changeTeam}
              hideLabel
            />
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn-primary-outlined" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn-primary" onClick={saveProject} disabled={!name.trim()}>Save</button>
        </div>
      </div>
    </Dialog>
  );
};
